<template>
  <div>
    <div class="d-flex align-center">
      <h2>帳票項目設定</h2>
      <v-spacer />
      <v-btn @click="openEditDialog()" :disabled="loading" color="primary" depressed>
        <v-icon left>mdi-plus</v-icon>
        新規作成
      </v-btn>
    </div>

    <!-- table -->
    <div class="mt-6">
      <v-data-table
        :headers="headers"
        :items="itemResponse ? itemResponse.data : []"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- name -->
        <template v-slot:[`item.type`]="{ item }">
          {{ getCategryText(item.type) }}
        </template>

        <!-- createdAt -->
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- edit -->
        <template v-slot:[`item.edit`]="{ item }">
          <v-btn @click="openEditDialog(item)" :disabled="loading" color="primary" variant="outlined" size="small">
            編集
          </v-btn>
        </template>

        <!-- delete -->
        <template v-slot:[`item.delete`]="{ item }">
          <v-btn @click="deleteFormItem(item.id)" :disabled="loading" color="error" size="small" depressed>
            削除
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title>帳票項目</v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6"> 項目名称* </v-col>
              <v-col cols="6">
                <v-text-field v-model="input.name" variant="outlined" hide-details="" density="compact"></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6"> 項目種類* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.type"
                  :items="categoryItems"
                  item-title="text"
                  item-value="value"
                  variant="outlined"
                  hide-details
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'childText'">
              <v-col cols="6"> クラス </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.classIdList"
                  :items="classes"
                  item-text="className"
                  item-value="classId"
                  multiple
                  chips
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'grid'">
              <v-col cols="6"> 行（縦に並ぶ項目） </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="headerInput.rows"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                  label="カンマ区切り"
                  placeholder="例) 項目1,項目2,..."
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'grid'">
              <v-col cols="6"> 列（横に並ぶ項目） </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="headerInput.cols"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                  label="カンマ区切り"
                  placeholder="例) 項目1,項目2,..."
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="editDialog = false" color="grey" text> キャンセル </v-btn>
              <v-spacer />
              <v-btn
                v-if="isUpdate"
                @click="updateFormItem"
                :disabled="loading"
                color="primary"
                depressed
              >
                保存
              </v-btn>
              <v-btn v-else @click="createFormItem" :disabled="loading" color="primary" depressed>
                作成
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { GuidancePlanFormItemCategoryItems } from "@/constants/guidance";
import {
  GuidancePlanItemCategoryType,
  NurseryGuidancePlanFormItemResponse,
  OperationGuidancePlanFormItemMastListResponse,
} from "chaild-api/lib";
import dayjs from "dayjs";
import { guidanceModule } from "@/store/dataModule/guidanceModule";
import LocalDataService from "@/service/LocalDataService";
import ApiGuidance from "@/api/ApiGuidance";

export default defineComponent({
  name: "GuidancePlanFormItem",

  async mounted() {
    await this.listFormItem();
  },

  data() {
    return {
      classes: [],
      loading: false,
      categoryItems: GuidancePlanFormItemCategoryItems,
      headers: [
        { title: "項目名", value: "name" },
        { title: "種類", value: "type" },
        { title: "作成日", value: "createdAt" },
        { title: "編集", value: "edit" },
        { title: "削除", value: "delete" },
      ],
      searchFilters: {
        limit: 3000,
        skip: 0,
      },
      input: {
        name: "",
        type: "" as GuidancePlanItemCategoryType,
        classIdList: [] as number[],
        itemMastId: 0 as number,
      },
      headerInput: {
        cols: "",
        rows: "",
      },
      editDialog: false,
    };
  },

  computed: {
    isUpdate: function (): boolean {
      return !!this.input.itemMastId;
    },
    itemResponse: function (): OperationGuidancePlanFormItemMastListResponse | null {
      return guidanceModule.itemMastList;
    },
  },

  methods: {
    async listFormItem() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        await guidanceModule.listItemMast({
          companyId,
          ...this.searchFilters,
        });
      }
      this.loading = false;
    },
    async createFormItem() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        // IDを除外したデータを作成
        const { itemMastId, ...payload } = this.input;

        try {
          const response = await ApiGuidance.createPlanFormItemMast({ // メソッド名が正しいか確認
            companyId,
            body: {
              ...payload,
              option: this.input.type === "grid"
                ? {
                    header: {
                      cols: this.getHeaderArray(this.headerInput.cols),
                      rows: this.getHeaderArray(this.headerInput.rows),
                    },
                  }
                : undefined,
            },
          });

          if (response) {
            this.editDialog = false;
            this.clearInput();
            await this.listFormItem();
          }
        } catch (e) {
          console.error("API Error:", e);
          // ここでエラーをキャッチしないと、ApiClientの eval で落ちる場合があります
        }
      }
      this.loading = false;
    },
    async updateFormItem() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        const response = await ApiGuidance.updatePlanFormItemMast({
          companyId,
          itemMastId: this.input.itemMastId,
          body: {
            ...this.input,
            option:
              this.input.type == "grid"
                ? {
                    header: {
                      cols: this.getHeaderArray(this.headerInput.cols),
                      rows: this.getHeaderArray(this.headerInput.rows),
                    },
                  }
                : undefined,
          },
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listFormItem();
        }
      }
      this.loading = false;
    },
    async deleteFormItem(itemMastId: string) {
      const answer = confirm("削除しますか？");
      if (answer) {
        this.loading = true;
        const companyId = LocalDataService.getCompanyId();
        if (companyId && itemMastId) {
          const response = await ApiGuidance.deletePlanFormItemMast({
            companyId,
            itemMastId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert("削除しました");
            await this.listFormItem();
          }
        }
        this.loading = false;
      }
    },
    openEditDialog(value?: NurseryGuidancePlanFormItemResponse) {
      if (value) {
        this.setInput(value);
      } else {
        this.clearInput();
      }
      this.editDialog = true;
    },
    getCategryText(value: string): string {
      const item = this.categoryItems.find((c) => c.value == value);
      return item?.text || "";
    },
    clearInput() {
      this.input = {
        name: "",
        type: "text",
        classIdList: [],
        itemMastId: 0,
      };
      this.headerInput = {
        cols: "",
        rows: "",
      };
    },
    setInput(value: NurseryGuidancePlanFormItemResponse) {
      this.input = {
        name: value.name,
        type: value.type,
        classIdList: value.classList?.map((c) => c.classId) || [],
        itemMastId: value.id,
      };
      if (value.type == "grid" && value.option) {
        this.headerInput = {
          cols: value.option.header.cols.join(","),
          rows: value.option.header.rows.join(","),
        };
      }
    },
    getHeaderArray(value: string): string[] {
      if (value) {
        return value.split(",");
      } else {
        return [];
      }
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("YYYY/MM/DD");
    },
  },
});
</script>
